import { DisbursementType, PaymentStatus } from '@willow/graphql-iso/src/app';

import { add, subtract } from './math';

export const getReimbursementAmounts = ({
  escrowBalance,
  suspenseBalance,
  paymentsMade,
  disbursements,
  payoffRecord,
}: {
  escrowBalance: number;
  suspenseBalance: number;
  paymentsMade: { id: string; status: PaymentStatus; total: number }[];
  disbursements: { type: DisbursementType; total: number; transactionId?: string | null }[];
  payoffRecord: { fee?: { lineItems: { type: string; amount: number; paymentId?: string | null }[] } | null };
}) => {
  // --- PPAP --- //
  // Pending payment amounts sit in suspense but they need to hang around until they process
  // so that we can apply that amount to the payoff if the payment fails
  const { ppapFeeTotal, ppapFeePending, ppapFeeDisbursed, ppapFeeReimbursementDue } = payoffRecord.fee
    ? payoffRecord.fee.lineItems
        .filter((li) => li.type === 'PENDING_PAYMENT_AT_PAYOFF')
        .reduce(
          (acc, li) => {
            const payment = paymentsMade.find((p) => p.id === li.paymentId);

            if (payment?.status === PaymentStatus.Pending) {
              acc.ppapFeePending = add(acc.ppapFeePending, li.amount);
            }

            if (payment?.status === PaymentStatus.Processed) {
              // Find how much has been disbursed
              const disbursedAmount = disbursements
                .filter((d) => d.type === DisbursementType.PaymentAfterPayoff && d.transactionId === payment?.id)
                .reduce((acc2, d) => add(acc2, d.total), 0);
              acc.ppapFeeDisbursed = add(acc.ppapFeeDisbursed, disbursedAmount);

              // We will only reimburse the amount that hasn't already been disbursed
              const remainingAmountToReimburse = subtract(li.amount, disbursedAmount);
              acc.ppapFeeReimbursementDue = add(acc.ppapFeeReimbursementDue, remainingAmountToReimburse);
            }

            acc.ppapFeeTotal = add(acc.ppapFeeTotal, li.amount);

            return acc;
          },
          {
            ppapFeeTotal: 0,
            ppapFeePending: 0,
            ppapFeeDisbursed: 0,
            ppapFeeReimbursementDue: 0,
          },
        )
    : {
        ppapFeeTotal: 0,
        ppapFeePending: 0,
        ppapFeeDisbursed: 0,
        ppapFeeReimbursementDue: 0,
      };

  const ppapFeeRemainingInSuspense = subtract(ppapFeeTotal ?? 0, ppapFeeDisbursed ?? 0);

  // --- SUSPENSE --- //
  // PPAP sits in suspense but is reimbursed separately, so we need to subtract it to find the isolated suspense reimbursement
  const suspenseWithoutPPAPFees = subtract(suspenseBalance, ppapFeeRemainingInSuspense ?? 0);
  // Negative suspense balance indicates waiver
  const suspenseReimbursement = suspenseWithoutPPAPFees > 0 ? suspenseWithoutPPAPFees : 0;

  // --- ESCROW --- //
  const escrowReimbursement = escrowBalance > 0 ? escrowBalance : 0;

  // --- TOTAL --- //
  const totalReimbursement = add(escrowReimbursement, suspenseReimbursement, ppapFeeReimbursementDue ?? 0);

  return {
    escrowReimbursement,
    ppapFeeTotal,
    ppapFeePending,
    ppapFeeReimbursementDue,
    suspenseReimbursement,
    totalReimbursement,
  };
};
